// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CharacterCountHelper from '../../../components/characterCountHelper/CharacterCountHelper';
import ContinueButton from '../../../components/continueButton/ContinueButton';
import './OrganizationRoute.less';
import { StorageKeys, SystemType } from '@api/constants';
import {
  confirmBusinessEmailVerification,
  getUploadUrl,
  isBusinessEmailVerified,
  orgInfoGet,
  requestBusinessEmailVerification,
  saveOrgInfo,
  uploadFile,
} from '@api/setupServices';
import {
  FileAccessModifier,
  type OrgInfoGetResponse,
  type PrepareFileUploadResponse,
} from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { featureSupported } from '@/featureFlagsUtils';
import { setToLocalStorage } from '@utils/utils';
import type { RcFile } from 'antd/es/upload';
import DefaultLogo from '../../../assets/default_logo.svg';
import LogoUpload from '../../../components/logoUpload/LogoUpload';
import type { ValidationRule } from '../../../components/validationRules/ValidationRules';
import { STEPS, TRANSLATION_BUTTON } from '../../constants';
import { useNavigateToNextStep } from '../../hooks/useNavigateToNextStep';
import { getBase64 } from '../../hooks/useOrganizationInfoSave';
import ConfirmOwnershipModal from '../../../components/otpChallenge/ConfirmOwnershipModal';
import { getSetupContainer } from '../../layout/SetupPageLayout';

enum FieldNames {
  ORG_NAME = 'orgName',
  EMAIL = 'email',
  LOGO = 'logo',
}

interface FormValues {
  [FieldNames.ORG_NAME]: string;
  [FieldNames.EMAIL]: string;
  [FieldNames.LOGO]: RcFile;
}

const TRANSLATION_ROOT = 'setup.org-info.content';
const TRANSLATION_ERRORS = 'setup.external-idp-info.content.validation';

const EMAIL_VALIDITY = {
  VALID: 'valid',
  INVALID_FORMAT: 'invalid-format',
  PUBLIC_PROVIDER: 'public-provider',
} as const;
type EmailValidity = (typeof EMAIL_VALIDITY)[keyof typeof EMAIL_VALIDITY];

const ORG_NAME_VALIDITY = { VALID: 'valid', REQUIRED: 'required' } as const;
type OrgNameValidity = (typeof ORG_NAME_VALIDITY)[keyof typeof ORG_NAME_VALIDITY];

const checkOrgName = (value: string): OrgNameValidity =>
  value.trim() === '' ? ORG_NAME_VALIDITY.REQUIRED : ORG_NAME_VALIDITY.VALID;

const PUBLIC_EMAIL_DOMAINS: string[] = [
  'gmail.com',
  'hotmail.com',
  'icloud.com',
  'outlook.com',
  'yahoo.com',
];

const OrganizationRoute = () => {
  const [isConfirmation, setIsConfirmation] = useState<boolean>(false);
  const [form] = Form.useForm<FormValues>();
  const [email, setEmail] = useState('');
  const [emailValidity, setEmailValidity] = useState<EmailValidity>(EMAIL_VALIDITY.VALID);
  const [orgNameValidity, setOrgNameValidity] = useState<OrgNameValidity>(ORG_NAME_VALIDITY.VALID);
  const navigateToNextStep = useNavigateToNextStep();
  const emailVerificationEnabled = featureSupported('EMAIL_VERIFICATION_ENABLED');
  const { emitError, clearErrorNotifications } = useErrorContext();
  const { t } = useTranslation();
  const [orgInfoGetResponse, setOrgInfoGetResponse] = useState<OrgInfoGetResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [codeIssuedAtMs, setCodeIssuedAtMs] = useState<number | null>(null);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState<boolean>(false);
  const [orgName, setOrgName] = useState('');
  const [orgNameFocused, setOrgNameFocused] = useState(false);
  const orgNameRequired = orgNameValidity === ORG_NAME_VALIDITY.REQUIRED;
  const orgNameHelperClassName = [
    'org-name-helper',
    orgNameRequired && 'org-name-helper--error',
    (orgNameFocused || orgNameRequired) && 'is-visible',
  ]
    .filter(Boolean)
    .join(' ');

  const emailRules: ValidationRule[] = [
    {
      id: 'email',
      label: t(`${TRANSLATION_ERRORS}.enter-valid-email`),
      validate: value => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

        if (!emailRegex.test(value)) {
          return false;
        }

        const [localPart, domain] = value.split('@');

        // Local part cannot start/end with '.' or contain '..'
        if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
          return false;
        }

        // Domain validation
        if (
          domain.startsWith('.') ||
          domain.endsWith('.') ||
          domain.startsWith('-') ||
          domain.endsWith('-') ||
          domain.includes('..') ||
          domain.includes('--')
        ) {
          return false;
        }

        // Only allowed characters
        if (!/^[\p{L}\p{N}._+-]+@[\p{L}\p{N}.-]+$/u.test(value)) {
          return false;
        }

        // Block restricted Unicode scripts
        const restrictedUnicodeRegex =
          /[\p{Script=Han}\p{Script=Hangul}\p{Script=Arabic}\p{Script=Hiragana}\p{Script=Katakana}]/u;

        if (restrictedUnicodeRegex.test(value)) {
          return false;
        }

        return true;
      },
    },
  ];

  const emailHelpKey = {
    [EMAIL_VALIDITY.VALID]: emailVerificationEnabled ? `${TRANSLATION_ROOT}.email-helper` : undefined,
    [EMAIL_VALIDITY.INVALID_FORMAT]: `${TRANSLATION_ERRORS}.enter-valid-email`,
    [EMAIL_VALIDITY.PUBLIC_PROVIDER]: `${TRANSLATION_ROOT}.validation.public-provider`,
  }[emailValidity];

  const isFormatValid = (value: string) => emailRules.every(rule => rule.validate(value));

  const checkEmail = (value: string): EmailValidity => {
    if (!isFormatValid(value)) {
      return EMAIL_VALIDITY.INVALID_FORMAT;
    }

    const domain = value.toLowerCase().split('@')[1];
    return PUBLIC_EMAIL_DOMAINS.includes(domain)
      ? EMAIL_VALIDITY.PUBLIC_PROVIDER
      : EMAIL_VALIDITY.VALID;
  };

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgInfo = await orgInfoGet();
      if (!orgInfo?.orgName) {
        setIsLoading(false);
        return;
      }
      setOrgInfoGetResponse(orgInfo);
      form.setFieldValue(FieldNames.ORG_NAME, orgInfo.orgName);
      form.setFieldValue(FieldNames.EMAIL, orgInfo.businessEmail);
      setOrgName(orgInfo.orgName);
      setEmail(orgInfo.businessEmail);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setIsLoading(false);
    }
  }, [form.setFieldValue, emitError]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const showForm = useCallback(() => {
    setIsConfirmation(false);
  }, []);

  const onConfirm = useCallback(async () => {
    try {
      setIsLoading(true);
      setToLocalStorage(StorageKeys.ORG_EMAIL, form.getFieldValue(FieldNames.EMAIL));

      let uploadLinks: PrepareFileUploadResponse | undefined;

      const logoFile: File = form.getFieldValue(FieldNames.LOGO);
      if (logoFile) {
        uploadLinks = await getUploadUrl(
          logoFile.name,
          FileAccessModifier.PUBLIC,
          SystemType.SETUP,
        );
        await uploadFile(logoFile, uploadLinks?.uploadUrl || '');
      }

      await saveOrgInfo({
        logoUrl: uploadLinks?.readUrl,
        orgName: form.getFieldValue(FieldNames.ORG_NAME),
        businessEmail: form.getFieldValue(FieldNames.EMAIL),
      });

      if (!emailVerificationEnabled || isBusinessEmailVerified(await orgInfoGet())) {
        navigateToNextStep(STEPS.IMPRIVATA_IDP);
        return;
      }

      const { otpId: newOtpId } = await requestBusinessEmailVerification();
      setOtpId(newOtpId);
      setCodeIssuedAtMs(Date.now());
      setIsVerifyModalOpen(true);
    } catch (error) {
      emitError(new AppError(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  }, [form, navigateToNextStep, emitError, emailVerificationEnabled]);

  const onVerified = useCallback(() => {
    setIsVerifyModalOpen(false);
    navigateToNextStep(STEPS.IMPRIVATA_IDP);
  }, [navigateToNextStep]);

  const closeVerifyModal = useCallback(() => {
    setIsVerifyModalOpen(false);
  }, []);

  const onResent = useCallback((newOtpId: string, issuedAtMs: number) => {
    setOtpId(newOtpId);
    setCodeIssuedAtMs(issuedAtMs);
  }, []);

  function showConfirmation() {
    const orgNameValue: string = form.getFieldValue(FieldNames.ORG_NAME) ?? '';
    const nameValidity = checkOrgName(orgNameValue);
    setOrgNameValidity(nameValidity);

    const validity = checkEmail(email);
    setEmailValidity(validity);

    if (nameValidity !== ORG_NAME_VALIDITY.VALID || validity !== EMAIL_VALIDITY.VALID) {
      return;
    }

    setIsConfirmation(true);
  }

  const closeBannerIfExists = () => {
    clearErrorNotifications();
  };

  // Rendering this way is important
  // If you remove and then show the form on back to form, you lose is dirty and Continue button gets disabled
  return (
    <>
      {isConfirmation && (
        <ConfirmScreen
          data={form.getFieldsValue()}
          oldImageUrl={orgInfoGetResponse?.logoUrl}
          goBack={showForm}
          onConfirm={onConfirm}
          isLoading={isLoading}
        />
      )}
      {emailVerificationEnabled && (
        <ConfirmOwnershipModal
          key={isVerifyModalOpen ? 'open' : 'closed'}
          getContainer={getSetupContainer}
          open={isVerifyModalOpen}
          email={form.getFieldValue(FieldNames.EMAIL)}
          otpId={otpId}
          codeIssuedAtMs={codeIssuedAtMs}
          effects={{
            request: requestBusinessEmailVerification,
            confirm: confirmBusinessEmailVerification,
          }}
          translationRoot="setup.org-info-confirm.otp"
          onResent={onResent}
          onVerified={onVerified}
          onCancel={closeVerifyModal}
        />
      )}
      <div className="org-info-step" style={{ display: isConfirmation ? 'none' : 'flex' }}>
        <Form className="org-info-form" form={form} layout="vertical" onFinish={showConfirmation}>
          <div className={'subtitle'} />

          {/* Organization Name */}
          <Form.Item
            name={FieldNames.ORG_NAME}
            className="org-name-form-item"
            validateStatus={orgNameValidity !== ORG_NAME_VALIDITY.VALID ? 'error' : undefined}
          >
            <InputBox
              label={t(`${TRANSLATION_ROOT}.name-label`)}
              required
              autoFocus
              value={orgName}
              minLength={3}
              maxLength={63}
              onFocus={() => setOrgNameFocused(true)}
              onChange={e => {
                const value = e.target.value;
                setOrgName(value);
                form.setFieldValue(FieldNames.ORG_NAME, value);
                if (orgNameValidity !== ORG_NAME_VALIDITY.VALID) {
                  setOrgNameValidity(checkOrgName(value));
                }
              }}
              onBlur={e => {
                setOrgNameFocused(false);
                setOrgNameValidity(checkOrgName(e.target.value));
              }}
            />
          </Form.Item>
          <div className={orgNameHelperClassName}>
            {orgNameValidity === ORG_NAME_VALIDITY.REQUIRED ? (
              t(`${TRANSLATION_ROOT}.validation.name-required`)
            ) : (
              <CharacterCountHelper
                text={t(`${TRANSLATION_ROOT}.name-helper`, { count: orgName.length })}
              />
            )}
          </div>

          <LogoUpload
            fieldName={FieldNames.LOGO}
            fieldValue={form.getFieldValue(FieldNames.LOGO)}
            oldImageUrl={orgInfoGetResponse?.logoUrl}
            isLoading={isLoading}
          />

          {/* Organization Email */}
          <Form.Item
            name={FieldNames.EMAIL}
            validateStatus={emailValidity !== EMAIL_VALIDITY.VALID ? 'error' : undefined}
            help={emailHelpKey && t(emailHelpKey)}
          >
            <InputBox
              label={t(`${TRANSLATION_ROOT}.email-label`)}
              required
              value={email}
              onChange={e => {
                const value = e.target.value;

                setEmail(value);
                form.setFieldValue(FieldNames.EMAIL, value);

                if (emailValidity !== EMAIL_VALIDITY.VALID) {
                  setEmailValidity(checkEmail(value));
                }
              }}
              onBlur={() => {
                setEmailValidity(checkEmail(email));
              }}
            />
          </Form.Item>

          {/* Continue */}
          <ContinueButton
            label={t(TRANSLATION_BUTTON)}
            htmlType="submit"
            style={{ marginTop: '40px' }}
            overrideErrorCheck={() => emailValidity !== EMAIL_VALIDITY.VALID}
            onClick={closeBannerIfExists}
            validateDirty={
              !orgInfoGetResponse?.orgName ? [FieldNames.ORG_NAME, FieldNames.EMAIL] : false
            }
          />
        </Form>
      </div>
    </>
  );
};

const ConfirmScreen = ({
  data,
  oldImageUrl,
  goBack,
  onConfirm,
  isLoading,
}: {
  data: FormValues;
  oldImageUrl?: string;
  goBack: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const TRANSLATION_ROOT = 'setup.org-info-confirm';
  const [imageUrl, setImageUrl] = useState<string | undefined>();

  useEffect(() => {
    if (data[FieldNames.LOGO]) {
      // no error handling since if this fails, it fails in the LogoUpload
      getBase64(data[FieldNames.LOGO]).then(url => setImageUrl(url));
    }
  }, [data[FieldNames.LOGO]]);

  return (
    <div className="confirm-org">
      <div className="confirm-org-intro">
        <p>{t(`${TRANSLATION_ROOT}.content.label`)}</p>
        <p>{t(`${TRANSLATION_ROOT}.content.name-logo-label`)}</p>
      </div>
      <div className="confirm-org-info">
        <div className="confirm-org-name-logo">
          <h2>{data?.[FieldNames.ORG_NAME]}</h2>
          <img className="confirm-org-logo" src={imageUrl || oldImageUrl || DefaultLogo} alt="org logo" />
        </div>
        <div className="confirm-org-email">
          <p className="confirm-org-email-label">{t(`${TRANSLATION_ROOT}.content.business-email-label`)}</p>
          <p className="confirm-org-email-value">{data?.[FieldNames.EMAIL]}</p>
        </div>
      </div>
      <Form className="confirm-org-buttons">
        <ContinueButton
          className="focus-outline"
          disabled={isLoading}
          autoFocus
          secondaryButton={{ label: t('common.back-button'), onClick: goBack, block: true }}
          onClick={onConfirm}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </Form>
    </div>
  );
};

export default OrganizationRoute;
